/**
 * Column recognition - the one table, shared by both import paths.
 *
 * There used to be two copies of this file: one in `packages/db` for
 * `pnpm db:import` and one in `apps/api` for the upload button, with the comment
 * "change one, change both" above the second. They drifted anyway. The db copy
 * normalised with NFKD (so `Oppervlakte m²` became `oppervlaktem2` and matched),
 * the API copy with NFD (so it became `oppervlaktem` and matched nothing) - and
 * the m2 column, and with it the price per m2 this whole application is about,
 * silently fell out of every file uploaded through the button.
 *
 * So there is one table now, in the package both sides already depend on.
 *
 * Matching is done on a normalised form (lower case, accents and punctuation
 * stripped), so "Vraagprijs" and "vraag prijs" land on the same field.
 *
 * Een kop draagt vaak méér dan de veldnaam: "Vraagprijs (EUR)", "Straat &
 * huisnr", "Prijs in euro". Die vielen naast elke alias, want de vergelijking
 * was één exacte opzoeking op de hele genormaliseerde naam - en omdat de
 * patroonherkenning alleen draaide als de kopregel in zijn geheel onbruikbaar
 * was, kwam zo'n bestand daar nooit aan toe en werd het in zijn geheel geweigerd
 * met een melding die niet klopte ("geen enkele rij heeft een vraagprijs").
 *
 * Daarom twee rondes: eerst de hele naam, voor alle kolommen, zodat een exacte
 * treffer altijd voorgaat. Daarna de losse woorden van de kop - "Vraagprijs
 * (EUR)" is `vraagprijs` + `eur` - waarbij de langste aaneengesloten reeks
 * woorden wint. Alleen hele woorden tellen mee: "Aantal badkamers" wordt géén
 * `kamers` en "Datum plaatsing" géén `plaats`.
 */
import type { HerkendeKolom } from './dataset';

/** Listing fields an import can fill. `prijsPerM2` is generated, never written. */
export type ImportVeld =
  | 'bronId'
  | 'bron'
  | 'bronUrl'
  | 'foto'
  | 'adres'
  | 'postcode'
  | 'plaats'
  | 'vraagprijs'
  | 'prijsConditie'
  | 'oppervlakteM2'
  | 'prijsPerM2'
  | 'kamers'
  | 'energielabel'
  | 'makelaar'
  | 'makelaarUrl'
  | 'badge'
  | 'pitch'
  | 'lat'
  | 'lng';

/**
 * Accepted spellings per field. The first entry is the canonical one; the rest
 * are what other exports of the same thing tend to be called.
 */
export const KOLOM_ALIASSEN: Record<ImportVeld, readonly string[]> = {
  bronId: ['bronid', 'id', 'objectid', 'fundaid', 'objectnummer'],
  bron: ['bron', 'portaal', 'source'],
  bronUrl: ['bronurl', 'url', 'link', 'objecturl', 'fundalink', 'detailurl'],
  foto: ['foto', 'afbeelding', 'image', 'imageurl', 'fotourl', 'thumbnail'],
  adres: ['adres', 'straat', 'straatnaam', 'address', 'straatenhuisnummer'],
  postcode: ['postcode', 'zipcode', 'pc'],
  plaats: ['plaats', 'stad', 'woonplaats', 'gemeente', 'city'],
  vraagprijs: ['vraagprijs', 'prijs', 'koopprijs', 'koopsom', 'price', 'askingprice'],
  prijsConditie: ['prijsconditie', 'conditie', 'kkvon', 'prijssoort'],
  oppervlakteM2: [
    'oppervlaktem2',
    'oppervlakte',
    'woonoppervlakte',
    'woonoppervlak',
    'woonopp',
    'opp',
    'm2',
    'wonen',
    'gebruiksoppervlakte',
  ],
  /* Recognised, but never written: the column is generated from price and area.
     Reporting it as unrecognised would suggest data was thrown away. */
  prijsPerM2: ['prijsperm2', 'prijspervierkantemeter', 'm2prijs', 'prijspm2'],
  kamers: ['kamers', 'aantalkamers', 'rooms', 'kamer'],
  energielabel: ['energielabel', 'label', 'energieklasse', 'energy'],
  makelaar: ['makelaar', 'makelaarsnaam', 'aanbieder', 'agent'],
  makelaarUrl: ['makelaarurl', 'makelaarslink', 'agenturl'],
  badge: ['badge', 'markering', 'tag', 'status'],
  pitch: ['pitch', 'omschrijving', 'samenvatting', 'beschrijving'],
  lat: ['lat', 'latitude', 'breedtegraad'],
  lng: ['lng', 'lon', 'long', 'longitude', 'lengtegraad'],
};

/** Lower case, accents dropped, everything but letters and digits removed. */
export function normaliseerKolomnaam(naam: string): string {
  return (
    naam
      /*
       * NFKD, not NFD. NFD pulls accents off letters but leaves `²` alone, and
       * that character was then dropped with the punctuation: the everyday Dutch
       * header "Oppervlakte m²" became `oppervlaktem`, matched no alias, and was
       * reported as unrecognised. NFKD turns it into a plain `2`.
       */
      .normalize('NFKD')
      .replace(/\p{Diacritic}/gu, '')
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '')
  );
}

const ALIAS_NAAR_VELD = new Map<string, ImportVeld>();
for (const [veld, aliassen] of Object.entries(KOLOM_ALIASSEN) as [
  ImportVeld,
  readonly string[],
][]) {
  for (const alias of aliassen) {
    if (!ALIAS_NAAR_VELD.has(alias)) ALIAS_NAAR_VELD.set(alias, veld);
  }
}

/**
 * The field this column name stands for, or null when we have no use for it.
 *
 * Eerst de hele naam, dan de woorden waaruit hij bestaat. `losseWoorden: false`
 * slaat die tweede ronde over, voor wie alleen een exacte treffer wil.
 */
export function veldVanKolomnaam(
  naam: string,
  opties: { losseWoorden?: boolean } = {},
): ImportVeld | null {
  const heel = ALIAS_NAAR_VELD.get(normaliseerKolomnaam(naam));
  if (heel) return heel;
  if (opties.losseWoorden === false) return null;
  return veldUitWoorden(naam);
}

/**
 * De kop in woorden hakken en de langste aaneengesloten reeks proberen.
 *
 * Gesplitst wordt op alles wat geen letter of cijfer is én op een overgang van
 * kleine naar hoofdletter, zodat `vraagPrijsEur` net zo uiteenvalt als
 * "Vraagprijs (EUR)". De langste reeks eerst: `prijs per m2` blijft daardoor
 * `prijsPerM2` en wordt geen `vraagprijs`.
 */
function veldUitWoorden(naam: string): ImportVeld | null {
  const woorden = naam
    .replace(/([\p{Ll}\p{N}])(\p{Lu})/gu, '$1 $2')
    .split(/[^\p{L}\p{N}]+/u)
    .map((woord) => normaliseerKolomnaam(woord))
    .filter((woord) => woord.length > 0);

  if (woorden.length < 2) return null;

  for (let lengte = woorden.length; lengte >= 1; lengte -= 1) {
    for (let start = 0; start + lengte <= woorden.length; start += 1) {
      const veld = ALIAS_NAAR_VELD.get(woorden.slice(start, start + lengte).join(''));
      if (veld) return veld;
    }
  }
  return null;
}

export type Kolomherkenning = {
  herkend: HerkendeKolom[];
  nietHerkend: string[];
  /** Source column name per field, for a row keyed by name (the JSON import). */
  veldNaarKolom: Partial<Record<ImportVeld, string>>;
  /** Column index per field, for a row that is a plain array (CSV and XLSX). */
  veldNaarIndex: Partial<Record<ImportVeld, number>>;
};

/**
 * Matches the columns of an incoming file against the listing fields.
 * A field that two columns claim keeps the first one; the second is reported as
 * unrecognised rather than silently overwriting it.
 *
 * `veldNaarKolom` keeps the column name exactly as it came in, because the JSON
 * path looks the value up with it; the report shows the trimmed name.
 */
export function herkenKolommen(kolommen: readonly string[]): Kolomherkenning {
  const gevonden: { kolom: string; veld: ImportVeld; index: number }[] = [];
  const nietHerkend: string[] = [];
  const veldNaarKolom: Partial<Record<ImportVeld, string>> = {};
  const veldNaarIndex: Partial<Record<ImportVeld, number>> = {};

  const neem = (kolom: string, naam: string, index: number, veld: ImportVeld): boolean => {
    if (veldNaarKolom[veld] !== undefined) return false;
    veldNaarKolom[veld] = kolom;
    veldNaarIndex[veld] = index;
    gevonden.push({ kolom: naam, veld, index });
    return true;
  };

  const gevuld = kolommen
    .map((kolom, index) => ({ kolom, naam: kolom.trim(), index }))
    .filter(({ naam }) => naam.length > 0);

  /* Ronde 1: de hele naam. Een exacte treffer gaat altijd voor op een kop waar
     het woord toevallig ook in staat, ongeacht de volgorde van de kolommen. */
  const rest = gevuld.filter(({ kolom, naam, index }) => {
    const veld = veldVanKolomnaam(naam, { losseWoorden: false });
    return !(veld && neem(kolom, naam, index, veld));
  });

  /* Ronde 2: de losse woorden van de kop - "Vraagprijs (EUR)", "Straat & huisnr". */
  for (const { kolom, naam, index } of rest) {
    const veld = veldVanKolomnaam(naam);
    if (!(veld && neem(kolom, naam, index, veld))) nietHerkend.push(naam);
  }

  /* In de volgorde van het bestand, ongeacht in welke ronde ze gevonden zijn. */
  gevonden.sort((a, b) => a.index - b.index);
  const herkend: HerkendeKolom[] = gevonden.map(({ kolom, veld }) => ({ kolom, veld }));

  return { herkend, nietHerkend, veldNaarKolom, veldNaarIndex };
}

/**
 * Is this first row a header row?
 *
 * A header row names fields: short words, no amounts, no URLs, no postcodes. We
 * ask for at least three recognised names and for no cell that reads as data -
 * otherwise an ordinary row with one recognisable word is read as a header and a
 * house disappears. Eén of twee bekende namen is te weinig om de kolommen mee te
 * benoemen, maar wel genoeg om te weten dat het geen woning is: zie
 * `isVermoedelijkeKopregel`.
 *
 * A currency symbol on its own is not data: "Vraagprijs (€)" is a perfectly
 * normal Dutch header. What settles it is whether there is an amount next to the
 * symbol, so the test asks for a digit as well.
 */
export function isKopregel(cellen: readonly string[]): boolean {
  return telBekendeNamen(cellen) >= 3;
}

/**
 * Een eerste rij die namen bevat in plaats van gegevens, maar te weinig bekende
 * namen om er de kolommen mee te benoemen.
 *
 * Zo'n rij ging als gewone rij door de patroonherkenning, en de tweede ronde -
 * die per kolom aanvult wat het bestand als geheel prijsgeeft - maakte er een
 * woning van: kop `Locatiekop;Postcode;Q3-waarde;...` leverde een woning
 * "Locatiekop" op met een vraagprijs van 3 (het cijfer uit "Q3-waarde"). Eén
 * verzonnen woning tussen tien echte is erger dan een rij minder.
 *
 * Er wordt minstens één bekende veldnaam gevraagd, zodat een echte datarij die
 * toevallig uit korte woorden bestaat niet alsnog sneuvelt.
 */
export function isVermoedelijkeKopregel(cellen: readonly string[]): boolean {
  const bekend = telBekendeNamen(cellen);
  return bekend >= 1 && bekend < 3;
}

/** Hoeveel cellen een veldnaam zijn - of -1 zodra een cel als gegeven leest. */
function telBekendeNamen(cellen: readonly string[]): number {
  const gevuld = cellen.filter((cel) => cel.trim().length > 0);
  if (gevuld.length < 3) return -1;

  const lijktData = gevuld.some(
    (cel) =>
      cel.startsWith('http') ||
      (/[€]/.test(cel) && /\d/.test(cel)) ||
      /\bk\.k\.|\bv\.o\.n\./i.test(cel) ||
      /^\d{4}\s?[A-Za-z]{2}\b/.test(cel) ||
      cel.length > 60,
  );
  if (lijktData) return -1;

  return gevuld.filter((cel) => veldVanKolomnaam(cel) !== null).length;
}
